import { createSlice } from "@reduxjs/toolkit";

// What a company's `account` entries hold (COMPANY_ACCOUNT_SELECT in the backend's company list)
const ACCOUNT_FIELDS = ["id", "email", "firstName", "lastName", "substituteForId"];

const accountEntry = account =>
  Object.fromEntries(
    ACCOUNT_FIELDS.filter(field => field in account).map(field => [field, account[field]]),
  );

const companySlice = createSlice({
  name: "company",
  initialState: [],
  reducers: {
    companyAdd(state, action) {
      return action.payload;
    },
    companyCreate(state, action) {
      const company = action.payload;
      state.push(company);
    },
    companyDelete(state, action) {
      const toDelete = state.findIndex(spec => spec.id === action.payload);
      if (toDelete > -1) state.splice(toDelete, 1);
    },
    companyEdit(state, action) {
      const company = action.payload;
      const companyIndex = state.findIndex(item => item.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      }
    },
    /**
     * The server's answer for an account the Admin-Bereich changed, so the company list shows
     * its accounts as they are now. With the account's `company` list (a company added or
     * removed) the account belongs to exactly those companies; without it (name or e-mail
     * address saved) only its entries change. A company loaded without its accounts stays as it is.
     */
    companyAccountChanged(state, action) {
      const account = action.payload;
      if (!account?.id || !Array.isArray(state)) return;
      const entry = accountEntry(account);
      const linkedIds = Array.isArray(account.company)
        ? new Set(account.company.map(company => company.id))
        : null;

      for (const company of state) {
        if (!Array.isArray(company?.account)) continue;
        const index = company.account.findIndex(item => item.id === account.id);
        const linked = linkedIds ? linkedIds.has(company.id) : index !== -1;
        if (!linked) {
          if (index !== -1) company.account.splice(index, 1);
        } else if (index === -1) {
          company.account.push(entry);
        } else {
          Object.assign(company.account[index], entry);
        }
      }
    },
    /** An account that belongs to no company any more (deleted, or a substitute detached). */
    companyAccountRemoved(state, action) {
      if (!Array.isArray(state)) return;
      for (const company of state) {
        if (!Array.isArray(company?.account)) continue;
        const index = company.account.findIndex(item => item.id === action.payload);
        if (index !== -1) company.account.splice(index, 1);
      }
    },
  },
});

export const {
  companyAdd,
  companyCreate,
  companyDelete,
  companyEdit,
  companyAccountChanged,
  companyAccountRemoved,
} = companySlice.actions;
export default companySlice.reducer;
